<template>
    <div class="work-pdf-document" :class="{ 'work-pdf-document--thumbnails': thumbnails }">
        <work-pdf-thumbnails v-if="thumbnails && thumbnailSource" :key="thumbnailEpoch" :source="thumbnailSource" :page="pageNumber" :order="pageOrder" :editable="reorderEnabled" :disabled="reorderBusy" :annotations="markupPreview" @page="goToPage" @move="reorderPages" />
        <form v-if="searchOpen" class="work-pdf-search" @submit.prevent="find(false)">
            <label>Cerca <input ref="search" v-model="query" type="search" @input="find(false, '')" /></label>
            <span role="status">{{ matches }}</span>
            <button type="button" aria-label="Risultato precedente" @click="find(true)">↑</button>
            <button type="submit" aria-label="Risultato successivo">↓</button>
            <button type="button" aria-label="Chiudi ricerca" @click="searchOpen = false">✕</button>
        </form>
        <div ref="container" class="work-pdf-document__scroll" tabindex="0" aria-label="Pagine del PDF" @wheel.prevent.stop="wheelZoom" @pointerdown="startPan" @pointermove="movePan" @pointerup="endPan" @pointercancel="endPan">
            <div ref="pages" class="pdfViewer" />
        </div>
    </div>
</template>

<script>
import { loadPdfEngine, preparePdfDocument } from "./pdfEngine";
import { pdfRotation } from "./pdfViewPreferences.mjs";
import { readPdfPageRotation, readScanOrientation } from "./pdfOrientation.mjs";
import WorkPdfThumbnails from "./WorkPdfThumbnails.vue";
import { pdfPointerNavigation } from "./pdfPointerNavigation.mjs";
import { createPdfApplication } from "./createPdfApplication.mjs";
import { fitPdfPage } from "./pdfPageFit.mjs";
import { guardPdfAnnotations } from "./pdfAnnotationGuard.mjs";
import "pdfjs-dist/web/pdf_viewer.css";

export default {
    name: "WorkPdfDocument",
    components: { WorkPdfThumbnails },
    props: { active: Boolean, generation: Number, url: String, prepared: Object, filename: String, context: Object, showThumbnails: { type: Boolean, default: true } },
    data: () => ({ searchOpen: false, query: "", matches: "", panEnabled: true, thumbnails: true, thumbnailSource: null, thumbnailEpoch: 0, pageNumber: 1, pageOrder: [], reorderEnabled: false, reorderBusy: false, markupPreview: [] }),
    computed: { documentIdentity() { return this.active && this.url ? `${this.generation}:${this.url}` : ""; } },
    watch: { documentIdentity(value) { if (value) this.open(); else this.clearDocument(); } },
    created() { this._abort = new AbortController(); this._documentId = 0; if (typeof this.showThumbnails === "boolean") this.thumbnails = this.showThumbnails; },
    mounted() {
        const warm = () => this.ensureViewer().catch(() => {});
        if (window.requestIdleCallback) this._warmId = window.requestIdleCallback(warm, { timeout: 2000 });
        else this._warmTimer = window.setTimeout(warm, 300);
        if (this.active && this.url) this.open();
    },
    beforeDestroy() {
        this._abort.abort();
        if (this._warmId !== undefined) window.cancelIdleCallback(this._warmId);
        window.clearTimeout(this._warmTimer);
        this._fitObserver?.disconnect();
        this.clearDocument();
    },
    methods: {
        reorderPages(move) { this._pdfEvents?.dispatch("reorderpages", move); },
        ...pdfPointerNavigation,
        ensureViewer() {
            if (!this._viewerReady) this._viewerReady = loadPdfEngine().then((engine) => this.initializeViewer(engine)).catch((error) => {
                this._viewerReady = null;
                throw error;
            });
            return this._viewerReady;
        },
        clearDocument() {
            this._documentId += 1;
            this._downloadAbort?.abort();
            this._application = null;
            this.endPan();
            this.thumbnailSource = null;
            this.markupPreview = [];
            this._thumbnailDocument = null;
            this.pageNumber = 1;
            this.pageOrder = []; this.reorderEnabled = false; this.reorderBusy = false;
            this._firstRotation = 0;
            this._scanPage = null;
            window.cancelAnimationFrame?.(this._orientationFrame);
            this._orientationFrame = null;
            this._orientationPage = null;
            window.cancelAnimationFrame?.(this._wheelFrame);
            this._viewer?.setDocument(null);
            this._link?.setDocument(null);
            this._task?.destroy().catch(() => {});
            this._task = null;
            this.searchOpen = false;
            this.query = "";
            this.matches = "";
            return this._documentId;
        },
        async initializeViewer({ api, viewer: components }) {
            await this.$nextTick();
            if (this._abort.signal.aborted) return;
            const eventBus = this._pdfEvents = new components.EventBus();
            const linkService = this._link = new components.PDFLinkService({ eventBus });
            const findController = new components.PDFFindController({ eventBus, linkService });
            const viewer = this._viewer = new components.PDFViewer({
                container: this.$refs.container, viewer: this.$refs.pages, eventBus, linkService,
                findController, abortSignal: this._abort.signal, annotationMode: api.AnnotationMode.ENABLE,
                downloadManager: new components.DownloadManager(),
                removePageBorders: true,
            });
            viewer.scrollMode = components.ScrollMode?.PAGE ?? 3;
            linkService.setViewer(viewer);
            if (typeof ResizeObserver !== "undefined") {
                this._fitObserver = new ResizeObserver(() => {
                    if (viewer.pagesCount && this.$refs.container.clientWidth > 0 &&
                        ["page-fit", "page-width", "auto"].includes(viewer.currentScaleValue)) {
                        const fitMode = viewer.currentScaleValue;
                        fitPdfPage(viewer, fitMode);
                    }
                });
                this._fitObserver.observe(this.$refs.container);
            }
            eventBus.on("updatefindmatchescount", ({ matchesCount }) => {
                this.matches = matchesCount.total ? `${matchesCount.current} / ${matchesCount.total}` : "Nessun risultato";
            });
            eventBus.on("updatefindcontrolstate", ({ state }) => {
                if (state === components.FindState.NOT_FOUND) this.matches = "Nessun risultato";
                else if (state === components.FindState.PENDING) this.matches = "Ricerca…";
            });
            eventBus.on("pagesinit", () => {
                const application = this._application;
                if (!application) return;
                // setDocument(null) resets PDF.js to vertical scrolling. Restore
                // one page before rotation/fit, including after saves/reopens.
                viewer.scrollMode = components.ScrollMode?.PAGE ?? 3;
                viewer.pagesRotation = this._firstRotation;
                fitPdfPage(viewer, "page-fit", 1);
                this.$emit("open", application);
            });
            eventBus.on("pagechanging", ({ pageNumber }) => { this.pageNumber = pageNumber; this.queueOrientation(pageNumber); });
            eventBus.on("rotationchanging", () => { this.thumbnailEpoch++; });
            eventBus.on("pagerendered", () => {
                this.queueThumbnails();
                this.orientScan();
            });
        },
        async open() {
            const requestId = this.clearDocument();
            const download = this._downloadAbort = new AbortController();
            const current = () => !this._abort.signal.aborted && requestId === this._documentId && this.active;
            try {
                await loadPdfEngine();
                if (!current()) return;
                const prepare = () => preparePdfDocument(this.url, undefined, { signal: download.signal });
                const prepared = this.prepared || await prepare();
                if (!current()) { await prepared.cancel?.(); return; }
                const task = prepared.take() || (await prepare()).take();
                if (!current()) { await task.destroy(); return; }
                this._task = task;
                await this.ensureViewer();
                const document = await task.promise;
                if (!current()) return;
                guardPdfAnnotations(document);
                if (document.getPage) {
                    const page = await document.getPage(1);
                    await readScanOrientation(document, this.context, this.$work, 1);
                    this._firstRotation = pdfRotation(this.context?.revision_id || document.fingerprints[0], await readPdfPageRotation(page));
                    if (!current()) return;
                }
                const viewer = this._viewer;
                const application = createPdfApplication(this, document);
                this._application = application;
                viewer.setDocument(document);
                this._link.setDocument(document);
            } catch (error) {
                if (current()) {
                    console.error("[PDF] Apertura fallita", error);
                    this.$emit("error");
                }
            }
        },
        queueThumbnails() {
            const application = this._application;
            if (!application || this.thumbnailSource || this._thumbnailDocument === application) return;
            this._thumbnailDocument = application;
            // Paint the full sheet before starting the smaller canvases.
            window.requestAnimationFrame(() => window.requestAnimationFrame(() => window.setTimeout(() => {
                if (this._application === application && !this._abort.signal.aborted) {
                    this.thumbnailSource = Object.freeze({ pdf: application.pdfDocument });
                }
            }, 0)));
        },
        queueOrientation(number) {
            if (!this._application || this._abort.signal.aborted) return;
            this._orientationPage = number;
            if (this._orientationFrame) return;
            this._orientationFrame = window.requestAnimationFrame(() => {
                this._orientationFrame = null;
                if (this._application) this.orientPage(this._orientationPage).catch(() => {});
            });
        },
        async orientPage(number) {
            const application = this._application;
            if (!application) return;
            const page = await application.pdfDocument.getPage(number);
            const rotation = pdfRotation(this.context?.revision_id || application.pdfDocument.fingerprints[0], await readPdfPageRotation(page), number);
            if (application !== this._application || number !== this._viewer.currentPageNumber) return;
            if (this._viewer.pagesRotation !== rotation) {
                this._viewer.pagesRotation = rotation;
                fitPdfPage(this._viewer, "page-fit", number);
            } else if (["page-fit", "page-width", "auto"].includes(this._viewer.currentScaleValue)) {
                // Native page dimensions can change without a rotation change.
                fitPdfPage(this._viewer, this._viewer.currentScaleValue, number);
            }
        },
        async orientScan() {
            const application = this._application, number = this._viewer?.currentPageNumber;
            if (!application || this._scanPage === number) return;
            this._scanPage = number;
            await readScanOrientation(application.pdfDocument, this.context, this.$work, number);
            if (this._application === application && this._viewer.currentPageNumber === number) this.queueOrientation(number);
        },
        async goToPage(number) {
            if (!this._application || number < 1 || number > this._application.pdfDocument.numPages) return;
            this._viewer.currentPageNumber = number;
        },
        find(previous, type = "again") {
            this._pdfEvents?.dispatch("find", { source: this, type, query: this.query,
                caseSensitive: false, entireWord: false, highlightAll: true, findPrevious: previous,
            });
        },
    },
};
</script>

<style>
.work-pdf-document { position: absolute; inset: 0; }
.work-pdf-document__scroll { position: absolute; inset: 6px; overflow: auto; }
.work-pdf-document--thumbnails .work-pdf-document__scroll { left: 122px; }
.work-pdf-document .pdfViewer { --page-bg-color: white; --page-margin: 0 auto; --page-border: 0; --pdfViewer-padding-bottom: 0; }
.work-pdf-document .pdfViewer.removePageBorders .page { margin: 0 auto; }
.work-pdf-document__scroll--dragging { cursor: grabbing; user-select: none; }
@media (max-width: 600px) { .work-pdf-document--thumbnails .work-pdf-document__scroll { left: 70px; } }
.work-pdf-search { position: absolute; z-index: 20; top: 8px; right: 20px; display: flex; align-items: center; gap: 8px; max-width: calc(100% - 40px); padding: 8px; background: #24343b; border: 1px solid #536c77; border-radius: 4px; }
.work-pdf-search input { color: white; background: #12191d; border: 1px solid #78909c; width: 170px; padding: 4px; }
.work-pdf-search button { color: white; min-width: 28px; min-height: 32px; }
@media (max-width: 400px) {
    .work-pdf-search { left: 8px; right: 8px; max-width: none; }
    .work-pdf-search label { min-width: 0; flex: 1; }
    .work-pdf-search input { max-width: 100%; width: 130px; }
    .work-pdf-search > span { position: absolute; top: 100%; left: 0; background: #24343b; }
}
</style>
